import { isBlankOrInvisible, scanStructure } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * Delete whatever follows a complete root value: `{"a":"b"} extra`.
 *
 * **Heuristic, because it deletes data** (ADR-0012). A JSON document holds exactly one
 * value, so something here is wrong — but not which side. The trailing text may be junk from
 * a copy-paste, or it may be the interesting half of a log line whose *first* value is the
 * accident, and nothing in the token stream can tell those apart. Deleting silently under
 * "Fix all" would be the tool quietly discarding the user's input, so this one is offered
 * and waits for a click.
 *
 * Extra closing brackets are excluded here: `{"a":1}}` has its own, safe rule.
 */
export const dropTrailingContentRule: RepairRule = {
  id: 'drop-trailing-content',
  confidence: 'heuristic',
  priority: 60,
  resolves: ['syntax/trailing-content'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const { trailingContent } = scanStructure(ctx.source, ctx.tokens);
    const first = trailingContent[0];
    const last = trailingContent[trailingContent.length - 1];
    if (first === undefined || last === undefined) return [];

    // Never propose deleting the whole document. "Trailing" presupposes a root value in
    // front of it, and when everything in front is invisible there is no root — only a
    // character that got read as one. `U+FEFF{"a":1}` took exactly this path and `repair`
    // returned the empty string, with the error count going DOWN so the driver's safety net
    // scored it a perfect repair. The scan no longer reads a BOM as a value; this is the
    // belt to that fix's braces, and it also covers the zero-width family the scan has no
    // reason to know about.
    if (isBlankOrInvisible(ctx.source.slice(0, first.from))) return [];

    return [
      {
        ruleId: 'drop-trailing-content',
        span: { from: first.from, to: last.to },
        edits: [{ from: first.from, to: last.to, insert: '' }],
        labelCode: 'fix.drop-trailing-content',
        labelParams: { length: last.to - first.from },
        resolves: 'syntax/trailing-content',
      },
    ];
  },
};
